import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaMale,
  FaFemale,
  FaArrowLeft,
  FaPen,
  FaCheck,
  FaSearch,
  FaBookOpen,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaEnvelope,
  FaSchool,
  FaUserGraduate,
} from "react-icons/fa";

import axiosInstance from "../../Context/axiosInstance";
import { DarkModeContext } from "../../Context/DarkModeContext";
import CustomSelect from "../../Components/CustomSelect";
import AcademicLevelSelect from "../../Components/AcademicLevelSelect";

const storeMap = {
  1: "Egypt",
  3: "UAE",
  4: "KSA",
};

const fieldClass = (isEditing, isDarkMode) =>
  `min-h-[46px] w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none transition ${
    isEditing
      ? isDarkMode
        ? "border-white/15 bg-[#292929] text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
        : "border-slate-200 bg-white text-slate-900 focus:border-[#06315B] focus:ring-2 focus:ring-[#06315B]/10"
      : isDarkMode
        ? "cursor-default border-transparent bg-white/5 text-gray-300"
        : "cursor-default border-transparent bg-slate-50 text-slate-600"
  }`;

export default function UserInfoPage() {
  const { isDarkMode } = useContext(DarkModeContext);
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [loadingUserCourses, setLoadingUserCourses] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const originalUserRef = useRef(null);

  const fetchUser = useCallback(async () => {
    setLoadingUserInfo(true);
    try {
      const res = await axiosInstance.get(`/users/${userId}`);
      const userData = res.data;

      const storeName = storeMap[userData.storeId];
      setUser({ ...userData, storeName });
      originalUserRef.current = userData;
    } catch (error) {
      toast.error("Failed to load user data");
    } finally {
      setLoadingUserInfo(false);
    }
  }, [userId]);

  const fetchCourses = useCallback(async () => {
    try {
      setLoadingUserCourses(true);
      const res = await axiosInstance.get(`/users/${userId}/courses`, {
        params: {
          page,
          pageSize: 4,
          status: "purchased",
          search: searchTerm || undefined,
        },
      });
      setCourses(res.data.response);
      setTotalCount(res.data.totalCount || 0);
    } catch (err) {
      toast.error("Failed to fetch user courses");
    } finally {
      setLoadingUserCourses(false);
    }
  }, [userId, searchTerm, page]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({
      ...prev,
      [name]: name === "academicLevelId" ? Number(value) : value,
    }));
  };

  const isUserChanged = () => {
    const original = originalUserRef.current;
    if (!original || !user) return false;

    const fieldsToCompare = [
      "name",
      "gender",
      "email",
      "guardianPhoneNumber",
      "phoneNumber",
      "address",
      "academicLevelId",
      "school",
      "governorate",
      "area",
      "storeId",
    ];

    return fieldsToCompare.some((key) => user[key] !== original[key]);
  };

  const handleSave = async () => {
    if (!user) return;

    if (!isUserChanged()) {
      toast.info("No changes detected — nothing to save.");
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put(`/users/${userId}`, {
        name: user.name,
        email: user.email,
        gender: user.gender,
        phoneNumber: user.phoneNumber,
        guardianPhoneNumber: user.guardianPhoneNumber,
        address: user.address,
        school: user.school,
        academicLevelId: Number(user.academicLevelId),
        governorate: user.governorate,
        area: user.area,
        storeId: user.storeId,
      });
      toast.success("User updated successfully");
      setIsEditing(false);
      originalUserRef.current = { ...user };
    } catch (error) {
      toast.error("Failed to update user info");
    } finally {
      setSaving(false);
    }
  };

  if (loadingUserInfo && loadingUserCourses) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ClipLoader size={60} color="#e11d48" />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen px-4 pb-12 pt-20 sm:px-6 lg:px-8 ${
        isDarkMode ? "bg-[#292929] text-white" : "bg-[#f6f7f9] text-slate-900"
      }`}
    >
      <div className="mx-auto max-w-[1450px]">
        {/* TOP BAR */}
        <div className="mb-7 flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`group inline-flex h-11 items-center gap-2 rounded-xl border px-4 text-sm font-semibold transition-all ${
              isDarkMode
                ? "border-white/10 bg-[#1e1e1e] text-gray-200 hover:bg-white/10"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            <FaArrowLeft
              className="text-xs transition-transform group-hover:-translate-x-1"
              aria-hidden="true"
            />
            Back
          </button>

          <div
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              isDarkMode
                ? "bg-white/5 text-gray-400"
                : "bg-white text-slate-500 shadow-sm"
            }`}
          >
            Student ID: {userId}
          </div>
        </div>

        {/* PROFILE HERO */}
        <div
          className={`mb-6 overflow-hidden rounded-3xl border ${
            isDarkMode
              ? "border-white/10 bg-[#1e1e1e]"
              : "border-slate-200 bg-white"
          }`}
        >
          <div
            className={`h-24 ${
              isDarkMode
                ? "bg-gradient-to-r from-red-900/50 via-[#292929] to-[#1e1e1e]"
                : "bg-gradient-to-r from-[#06315B] via-[#0b477f] to-[#2264E5]"
            }`}
          />

          <div className="flex flex-col gap-5 px-5 pb-6 sm:flex-row sm:items-end sm:px-7">
            <div
              className={`-mt-11 flex h-24 w-24 shrink-0 items-center justify-center rounded-3xl border-4 text-4xl shadow-lg ${
                isDarkMode
                  ? "border-[#1e1e1e] bg-[#292929] text-red-400"
                  : "border-white bg-slate-100 text-[#06315B]"
              }`}
            >
              {user?.gender === "Male" ? <FaMale /> : <FaFemale />}
            </div>

            <div className="min-w-0 flex-1 sm:pb-1">
              <h1 className="truncate text-2xl font-bold sm:text-3xl">
                {user?.name || "Student"}
              </h1>

              <div
                className={`mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm ${
                  isDarkMode ? "text-gray-400" : "text-slate-500"
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <FaEnvelope className="text-xs" />
                  {user?.email || "No email"}
                </span>

                <span className="inline-flex items-center gap-2">
                  <FaPhoneAlt className="text-xs" />
                  {user?.phoneNumber || "No phone"}
                </span>

                <span className="inline-flex items-center gap-2">
                  <FaMapMarkerAlt className="text-xs" />
                  {user?.storeName || "Unknown store"}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                if (isEditing) handleSave();
                else setIsEditing(true);
              }}
              disabled={saving}
              className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
                isDarkMode
                  ? "bg-customRed hover:bg-red-600"
                  : "bg-[#06315B] hover:bg-[#0b477f]"
              }`}
            >
              {saving ? (
                <>
                  <ClipLoader size={15} color="#fff" />
                  Saving...
                </>
              ) : isEditing ? (
                <>
                  <FaCheck />
                  Save Changes
                </>
              ) : (
                <>
                  <FaPen />
                  Edit Profile
                </>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)]">
          {/* STUDENT INFORMATION */}
          <section
            className={`rounded-3xl border p-5 sm:p-7 ${
              isDarkMode
                ? "border-white/10 bg-[#1e1e1e]"
                : "border-slate-200 bg-white"
            }`}
          >
            <div className="mb-6 flex items-center justify-between">
              <div>
                <p
                  className={`text-xs font-semibold uppercase tracking-[0.16em] ${
                    isDarkMode ? "text-gray-500" : "text-slate-400"
                  }`}
                >
                  Student Profile
                </p>

                <h2 className="mt-1 text-xl font-bold">Personal Information</h2>
              </div>

              {isEditing && (
                <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                  Editing
                </span>
              )}
            </div>

            {user && (
              <div className="space-y-7">
                {/* BASIC */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <FaUserGraduate
                      className={isDarkMode ? "text-red-400" : "text-[#06315B]"}
                    />
                    <h3 className="text-sm font-bold">Basic Information</h3>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Full Name
                      </label>

                      <input
                        type="text"
                        name="name"
                        value={user.name || ""}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Gender
                      </label>

                      <CustomSelect
                        name="gender"
                        value={user.gender}
                        onChange={handleChange}
                        disabled={!isEditing}
                        isDarkMode={isDarkMode}
                        isEditing={isEditing}
                        options={[
                          {
                            value: "",
                            label: "Select Gender",
                            disabled: true,
                          },
                          { value: "Male", label: "Male" },
                          { value: "Female", label: "Female" },
                        ]}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Store
                      </label>

                      <CustomSelect
                        name="storeId"
                        value={user.storeId}
                        onChange={handleChange}
                        disabled={!isEditing}
                        isDarkMode={isDarkMode}
                        isEditing={isEditing}
                        options={[
                          {
                            value: "",
                            label: "Select Store",
                            disabled: true,
                          },
                          { value: 1, label: "Egypt" },
                          // { value: 2, label: "Netherlands" },
                          { value: 3, label: "UAE" },
                          { value: 4, label: "Saudi Arabia" },
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {/* CONTACT */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <FaPhoneAlt
                      className={isDarkMode ? "text-red-400" : "text-[#06315B]"}
                    />
                    <h3 className="text-sm font-bold">Contact Details</h3>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Email Address
                      </label>

                      <input
                        type="email"
                        name="email"
                        value={user.email || ""}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Phone Number
                      </label>

                      <input
                        type="text"
                        name="phoneNumber"
                        value={user.phoneNumber || ""}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Guardian Phone
                      </label>

                      <input
                        type="text"
                        name="guardianPhoneNumber"
                        value={user.guardianPhoneNumber || ""}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>
                  </div>
                </div>

                {/* EDUCATION */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <FaSchool
                      className={isDarkMode ? "text-red-400" : "text-[#06315B]"}
                    />
                    <h3 className="text-sm font-bold">Education</h3>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Academic Level
                      </label>

                      <AcademicLevelSelect
                        name="academicLevelId"
                        value={user.academicLevelId}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        University or School
                      </label>

                      <input
                        type="text"
                        name="school"
                        value={user.school || ""}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>
                  </div>
                </div>

                {/* ADDRESS */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <FaMapMarkerAlt
                      className={isDarkMode ? "text-red-400" : "text-[#06315B]"}
                    />
                    <h3 className="text-sm font-bold">Location</h3>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Address
                      </label>

                      <input
                        type="text"
                        name="address"
                        value={user.address || ""}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Governorate
                      </label>

                      <input
                        type="text"
                        name="governorate"
                        value={user.governorate || ""}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-medium opacity-60">
                        Area
                      </label>

                      <input
                        type="text"
                        name="area"
                        value={user.area || ""}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={fieldClass(isEditing, isDarkMode)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* COURSES */}
          <section
            className={`flex min-h-[600px] flex-col rounded-3xl border p-5 sm:p-7 ${
              isDarkMode
                ? "border-white/10 bg-[#1e1e1e]"
                : "border-slate-200 bg-white"
            }`}
          >
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <p
                  className={`text-xs font-semibold uppercase tracking-[0.16em] ${
                    isDarkMode ? "text-gray-500" : "text-slate-400"
                  }`}
                >
                  Learning Library
                </p>

                <h2 className="mt-1 text-xl font-bold">Purchased Courses</h2>
              </div>

              <span
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  isDarkMode
                    ? "bg-white/5 text-gray-300"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {totalCount} {totalCount === 1 ? "course" : "courses"}
              </span>
            </div>

            {/* SEARCH */}
            <div className="relative mb-5">
              <FaSearch
                className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm ${
                  isDarkMode ? "text-gray-500" : "text-slate-400"
                }`}
              />

              <input
                type="search"
                placeholder="Search purchased courses..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className={`min-h-[48px] w-full rounded-xl border py-3 pl-11 pr-4 text-sm outline-none transition ${
                  isDarkMode
                    ? "border-white/10 bg-[#292929] text-white placeholder:text-gray-500 focus:border-red-500"
                    : "border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:border-[#06315B] focus:bg-white"
                }`}
              />
            </div>

            {/* COURSE CONTENT */}
            <div className="relative flex-1">
              {loadingUserCourses ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <ClipLoader
                    size={42}
                    color={isDarkMode ? "#FF1F39" : "#06315B"}
                  />
                </div>
              ) : courses.length === 0 ? (
                <div className="flex h-full min-h-[350px] flex-col items-center justify-center px-5 text-center">
                  <div
                    className={`mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${
                      isDarkMode ? "bg-white/5" : "bg-slate-100"
                    }`}
                  >
                    <FaBookOpen className="text-2xl opacity-60" />
                  </div>

                  <h3 className="font-bold">
                    {searchTerm ? "No matching courses" : "No purchases yet"}
                  </h3>

                  <p
                    className={`mt-2 max-w-xs text-sm ${
                      isDarkMode ? "text-gray-500" : "text-slate-500"
                    }`}
                  >
                    {searchTerm
                      ? "Try searching for another course."
                      : "Courses purchased by this student will appear here."}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {courses.map((course) => (
                    <div
                      key={course.id}
                      className={`group overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                        isDarkMode
                          ? "border-white/10 bg-[#292929]"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="relative aspect-[1.65] overflow-hidden">
                        <img
                          src={course.courseImageUrl || "/default-course.jpg"}
                          alt={course.name}
                          onError={(event) => {
                            event.currentTarget.src = "/default-course.jpg";
                          }}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                        <span className="absolute bottom-3 left-3 rounded-full bg-emerald-500/90 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur-sm">
                          Purchased
                        </span>
                      </div>

                      <div className="p-4">
                        <h3 className="line-clamp-2 min-h-[48px] font-bold leading-snug">
                          {course.name}
                        </h3>

                        <div className="mt-3 flex items-center justify-between">
                          <span
                            className={`text-xs ${
                              isDarkMode ? "text-gray-500" : "text-slate-500"
                            }`}
                          >
                            Course price
                          </span>

                          <span className="text-sm font-bold">
                            {course.price} EGP
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* PAGINATION */}
            {totalCount > 4 && !loadingUserCourses && (
              <div
                className={`mt-6 flex items-center justify-between border-t pt-5 ${
                  isDarkMode ? "border-white/10" : "border-slate-200"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-30 ${
                    isDarkMode
                      ? "bg-white/5 hover:bg-white/10"
                      : "bg-slate-100 hover:bg-slate-200"
                  }`}
                >
                  ← Previous
                </button>

                <span
                  className={`text-xs font-medium ${
                    isDarkMode ? "text-gray-400" : "text-slate-500"
                  }`}
                >
                  Page {page} of {Math.ceil(totalCount / 4)}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setPage((prev) =>
                      prev < Math.ceil(totalCount / 4) ? prev + 1 : prev,
                    )
                  }
                  disabled={page >= Math.ceil(totalCount / 4)}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-30 ${
                    isDarkMode
                      ? "bg-white/5 hover:bg-white/10"
                      : "bg-slate-100 hover:bg-slate-200"
                  }`}
                >
                  Next →
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// import AcademicLevelSelect from "../../Components/AcademicLevelSelect";
// import React, {
//   useEffect,
//   useState,
//   useContext,
//   useRef,
//   useCallback,
// } from "react";
// import { FaMale, FaFemale } from "react-icons/fa";
// import { ClipLoader } from "react-spinners";
// import axiosInstance from "../../Context/axiosInstance";
// import { toast } from "react-toastify";
// import { useParams, useNavigate } from "react-router-dom";
// import { DarkModeContext } from "../../Context/DarkModeContext";
// import CustomSelect from "../../Components/CustomSelect";

// const storeMap = {
//   1: "Egypt",
//   3: "UAE",
//   4: "KSA",
// };

// export default function UserInfoPage() {
//   const { isDarkMode } = useContext(DarkModeContext);
//   const { userId } = useParams();
//   const navigate = useNavigate();

//   const [user, setUser] = useState(null);
//   const [courses, setCourses] = useState([]);
//   const [loadingUserInfo, setLoadingUserInfo] = useState(true);
//   const [loadingUserCourses, setLoadingUserCourses] = useState(true);
//   const [saving, setSaving] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);

//   const [page, setPage] = useState(1);
//   const [totalCount, setTotalCount] = useState(0);
//   const [searchTerm, setSearchTerm] = useState("");

//   const originalUserRef = useRef(null);

//   const fetchUser = useCallback(async () => {
//     setLoadingUserInfo(true);
//     try {
//       const res = await axiosInstance.get(`/users/${userId}`);
//       const userData = res.data;

//       const storeName = storeMap[userData.storeId];
//       setUser({ ...userData, storeName });
//       originalUserRef.current = userData;
//     } catch (error) {
//       toast.error("Failed to load user data");
//     } finally {
//       setLoadingUserInfo(false);
//     }
//   }, [userId]);

//   const fetchCourses = useCallback(async () => {
//     try {
//       setLoadingUserCourses(true);
//       const res = await axiosInstance.get(`/users/${userId}/courses`, {
//         params: {
//           page,
//           pageSize: 4,
//           status: "purchased",
//           search: searchTerm || undefined,
//         },
//       });
//       setCourses(res.data.response);
//       setTotalCount(res.data.totalCount || 0);
//     } catch (err) {
//       toast.error("Failed to fetch user courses");
//     } finally {
//       setLoadingUserCourses(false);
//     }
//   }, [userId, searchTerm, page]);

//   useEffect(() => {
//     fetchUser();
//   }, [fetchUser]);

//   useEffect(() => {
//     fetchCourses();
//   }, [fetchCourses]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setUser((prev) => ({ ...prev, [name]: name === "academicLevelId" ? Number(value) : value }));
//   };

//   const isUserChanged = () => {
//     const original = originalUserRef.current;
//     if (!original || !user) return false;

//     const fieldsToCompare = [
//       "name",
//       "gender",
//       "email",
//       "guardianPhoneNumber",
//       "phoneNumber",
//       "address",
//       "academicLevelId",
//       "school",
//       "governorate",
//       "area",
//       "storeId",
//     ];

//     return fieldsToCompare.some((key) => user[key] !== original[key]);
//   };

//   const handleSave = async () => {
//     if (!user) return;

//     if (!isUserChanged()) {
//       toast.info("No changes detected — nothing to save.");
//       setIsEditing(false);
//       return;
//     }

//     setSaving(true);
//     try {
//       await axiosInstance.put(`/users/${userId}`, {
//         name: user.name,
//         email: user.email,
//         gender: user.gender,
//         phoneNumber: user.phoneNumber,
//         guardianPhoneNumber: user.guardianPhoneNumber,
//         address: user.address,
//         school: user.school,
//         academicLevelId: Number(user.academicLevelId),
//         governorate: user.governorate,
//         area: user.area,
//         storeId: user.storeId,
//       });
//       toast.success("User updated successfully");
//       setIsEditing(false);
//       originalUserRef.current = { ...user };
//     } catch (error) {
//       toast.error("Failed to update user info");
//     } finally {
//       setSaving(false);
//     }
//   };

//   if (loadingUserInfo && loadingUserCourses) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <ClipLoader size={60} color="#e11d48" />
//       </div>
//     );
//   }

//   return (
//     <div
//       className={`${
//         isDarkMode ? "bg-[#292929] text-white" : "bg-gray-50 text-black"
//       } min-h-screen p-4 sm:p-6 lg:p-8 lg:pt-12 animate-zoomIn`}
//     >
//       {/* Top Controls */}
//       <div className="flex flex-wrap gap-3 justify-between items-center max-w-screen-lg mx-auto mb-6">
//         <button
//           onClick={() => navigate(-1)}
//           className={`px-4 py-2 rounded-md ${
//             isDarkMode
//               ? "bg-gray-700 hover:bg-gray-600 text-white"
//               : "bg-gray-200 hover:bg-gray-300 text-black"
//           }`}
//         >
//           ← Back
//         </button>
//       </div>

//       <div className="flex flex-col lg:flex-row gap-4 max-w-screen-lg mx-auto">
//         {/* User Info Card */}
//         <div
//           className={`${
//             isDarkMode
//               ? "bg-[#1e1e1e] border-2 border-customRed text-white"
//               : "bg-white text-black"
//           } rounded-lg shadow-lg flex-1 p-6`}
//         >
//           <div className="relative flex justify-center mb-4">
//             {user.gender === "Male" ? (
//               <FaMale className="w-20 h-20 pt-2" />
//             ) : (
//               <FaFemale className="w-20 h-20 pt-2" />
//             )}
//           </div>

//           <h1 className="text-2xl font-bold mb-4 text-center">Information</h1>

//           {user && (
//             <div className="space-y-3">
//               {/* Fields in the exact order */}
//               <CustomSelect
//                 name="storeId"
//                 value={user.storeId}
//                 onChange={handleChange}
//                 disabled={!isEditing}
//                 isDarkMode={isDarkMode}
//                 isEditing={isEditing}
//                 options={[
//                   { value: "", label: "Select Store", disabled: true },
//                   { value: 1, label: "Egypt" },
//                   { value: 2, label: "Netherlands" },
//                   { value: 3, label: "UAE" },
//                   { value: 4, label: "Saudi Arabia" },
//                 ]}
//               />

//               <input
//                 type="text"
//                 name="name"
//                 value={user.name || ""}
//                 onChange={handleChange}
//                 placeholder="Full Name"
//                 disabled={!isEditing}
//                 className={`w-full border p-2 rounded ${
//                   !isEditing ? "opacity-60 cursor-not-allowed" : ""
//                 } ${
//                   isEditing &&
//                   isDarkMode &&
//                   "bg-transparent border border-customRed"
//                 }`}
//               />

//               <CustomSelect
//                 name="gender"
//                 value={user.gender}
//                 onChange={handleChange}
//                 disabled={!isEditing}
//                 isDarkMode={isDarkMode}
//                 isEditing={isEditing}
//                 options={[
//                   { value: "", label: "Select Gender", disabled: true },
//                   { value: "Male", label: "Male" },
//                   { value: "Female", label: "Female" },
//                 ]}
//               />

//               <input
//                 type="text"
//                 name="email"
//                 value={user.email || ""}
//                 onChange={handleChange}
//                 placeholder="Email"
//                 disabled={!isEditing}
//                 className={`w-full border p-2 rounded ${
//                   !isEditing ? "opacity-60 cursor-not-allowed" : ""
//                 } ${
//                   isEditing &&
//                   isDarkMode &&
//                   "bg-transparent border border-customRed"
//                 }`}
//               />

//               <input
//                 type="text"
//                 name="guardianPhoneNumber"
//                 value={user.guardianPhoneNumber || ""}
//                 onChange={handleChange}
//                 placeholder="Guardian’s Phone Number"
//                 disabled={!isEditing}
//                 className={`w-full border p-2 rounded ${
//                   !isEditing ? "opacity-60 cursor-not-allowed" : ""
//                 } ${
//                   isEditing &&
//                   isDarkMode &&
//                   "bg-transparent border border-customRed"
//                 }`}
//               />

//               <input
//                 type="text"
//                 name="phoneNumber"
//                 value={user.phoneNumber || ""}
//                 onChange={handleChange}
//                 placeholder="Phone Number"
//                 disabled={!isEditing}
//                 className={`w-full border p-2 rounded ${
//                   !isEditing ? "opacity-60 cursor-not-allowed" : ""
//                 } ${
//                   isEditing &&
//                   isDarkMode &&
//                   "bg-transparent border border-customRed"
//                 }`}
//               />

//               <input
//                 type="text"
//                 name="address"
//                 value={user.address || ""}
//                 onChange={handleChange}
//                 placeholder="Address"
//                 disabled={!isEditing}
//                 className={`w-full border p-2 rounded ${
//                   !isEditing ? "opacity-60 cursor-not-allowed" : ""
//                 } ${
//                   isEditing &&
//                   isDarkMode &&
//                   "bg-transparent border border-customRed"
//                 }`}
//               />

//               <AcademicLevelSelect name="academicLevelId" value={user.academicLevelId} onChange={handleChange} disabled={!isEditing} className={`w-full border p-2 rounded ${isDarkMode ? "bg-[#292929] text-white" : "bg-white text-black"}`} />

//               <input
//                 type="text"
//                 name="school"
//                 value={user.school || ""}
//                 onChange={handleChange}
//                 placeholder="School"
//                 disabled={!isEditing}
//                 className={`w-full border p-2 rounded ${
//                   !isEditing ? "opacity-60 cursor-not-allowed" : ""
//                 } ${
//                   isEditing &&
//                   isDarkMode &&
//                   "bg-transparent border border-customRed"
//                 }`}
//               />

//               <input
//                 type="text"
//                 name="governorate"
//                 value={user.governorate || ""}
//                 onChange={handleChange}
//                 placeholder="Governorate"
//                 disabled={!isEditing}
//                 className={`w-full border p-2 rounded ${
//                   !isEditing ? "opacity-60 cursor-not-allowed" : ""
//                 } ${
//                   isEditing &&
//                   isDarkMode &&
//                   "bg-transparent border border-customRed"
//                 }`}
//               />

//               <input
//                 type="text"
//                 name="area"
//                 value={user.area || ""}
//                 onChange={handleChange}
//                 placeholder="Area"
//                 disabled={!isEditing}
//                 className={`w-full border p-2 rounded ${
//                   !isEditing ? "opacity-60 cursor-not-allowed" : ""
//                 } ${
//                   isEditing &&
//                   isDarkMode &&
//                   "bg-transparent border border-customRed"
//                 }`}
//               />
//             </div>
//           )}

//           <div className="flex justify-center pt-4">
//             <button
//               onClick={() => {
//                 if (isEditing) handleSave();
//                 else setIsEditing(true);
//               }}
//               disabled={saving}
//               className={`px-4 py-2 rounded-md ${
//                 isDarkMode
//                   ? "bg-customRed hover:bg-red-600 text-white"
//                   : "bg-blue-600 hover:bg-blue-700 text-white"
//               }`}
//             >
//               {saving ? "Saving..." : isEditing ? "Save" : "Edit"}
//             </button>
//           </div>
//         </div>

//         {/* Courses Card*/}
//         <div
//           className={`${
//             isDarkMode
//               ? "bg-[#1e1e1e] border-2 border-customRed text-white"
//               : "bg-white text-black"
//           } rounded-lg shadow-lg flex-1 p-6 flex flex-col`}
//         >
//           <h1 className="text-2xl font-bold mb-4 text-center">Courses</h1>

//           {courses.length > 0 && (
//             <div className="flex w-full mb-4">
//               {/* Search */}
//               <input
//                 type="text"
//                 placeholder="Search courses..."
//                 value={searchTerm}
//                 onChange={(e) => {
//                   setSearchTerm(e.target.value);
//                   setPage(1); // reset to first page on search
//                 }}
//                 className={`w-full px-3 py-2 rounded-full border focus:outline-none ${
//                   isDarkMode
//                     ? "bg-[#2a2a2a] border-gray-700 text-white"
//                     : "bg-white border-gray-300 text-black"
//                 }`}
//               />
//             </div>
//           )}

//           {loadingUserCourses ? (
//             <div className="flex-1 flex items-center justify-center">
//               <ClipLoader
//                 size={30}
//                 color={`${isDarkMode ? "#FF1F39" : "#06315B"}`}
//               />
//             </div>
//           ) : courses.length === 0 ? (
//             <div className="flex-1 flex items-center justify-center opacity-70">
//               <p>The student hasn’t purchased anything yet.</p>
//             </div>
//           ) : (
//             <div className="flex-1 animate-fadeIn overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent pr-2">
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                 {courses.map((course) => (
//                   <div
//                     key={course.id}
//                     className="border rounded-lg p-3 flex flex-col items-center"
//                   >
//                     <img
//                       src={course.courseImageUrl}
//                       alt={course.name}
//                       className="w-full h-32 object-cover rounded-md mb-2"
//                     />
//                     <p className="font-semibold text-center">{course.name}</p>
//                     <p
//                       className={`text-sm ${
//                         course.purchased ? "text-green-500" : "text-red-500"
//                       }`}
//                     >
//                       {course.purchased ? "Purchased" : "Not Purchased"}
//                     </p>
//                     <p className="text-sm font-medium mt-1">
//                       Price: {course.price} EGP
//                     </p>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}

//           {/* Pagination pinned at bottom */}

//           {totalCount > 4 && (
//             <div className="flex justify-between items-center mt-4 border-t pt-3">
//               <button
//                 onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
//                 disabled={page === 1}
//                 className={`px-4 py-1 rounded-md font-medium transition ${
//                   isDarkMode
//                     ? "bg-customRed hover:bg-red-600 disabled:bg-gray-800 text-white"
//                     : "bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-black"
//                 }`}
//               >
//                 ← Prev
//               </button>

//               <span className="text-sm font-medium opacity-80">
//                 Page {page} / {Math.ceil(totalCount / 4)}
//               </span>

//               <button
//                 onClick={() =>
//                   setPage((prev) =>
//                     prev < Math.ceil(totalCount / 4) ? prev + 1 : prev
//                   )
//                 }
//                 disabled={page >= Math.ceil(totalCount / 4)}
//                 className={`px-4 py-1 rounded-md font-medium transition ${
//                   isDarkMode
//                     ? "bg-customRed hover:bg-red-600 disabled:bg-gray-800 text-white"
//                     : "bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-black"
//                 }`}
//               >
//                 Next →
//               </button>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }
